import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useBranding } from "../../context/BrandingContext";
import VideoEmbed from "./VideoEmbed";

/**
 * ProductReelsRail — PDP "Seen in Reels" rail.
 * Shows reels that tagged this product. Silent hide when empty OR when
 * THIS tenant's Apple 5.2.3 kill-switch is off — super-admin controlled
 * (`tenants.reels_enabled === false`, surfaced as `branding.reels_enabled`).
 */
export default function ProductReelsRail({ productId }) {
  const { branding } = useBranding();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    // Skip the API call entirely when Reels are globally disabled —
    // saves a round-trip AND ensures the endpoint's silent-degrade
    // never fires in the first place.
    if (branding?.reels_enabled === false) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/social/by-product/${productId}?limit=6`);
        if (alive) setRows(data?.items || []);
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [productId, branding?.reels_enabled]);

  if (loading || rows.length === 0 || branding?.reels_enabled === false) return null;

  return (
    <div className="mt-16" data-testid="pdp-reels-rail">
      <div className="overline mb-2">See this in action</div>
      <h2 className="font-display text-3xl mb-6">As seen in reels</h2>
      <div className="flex gap-4 overflow-x-auto snap-x pb-4 -mx-2 px-2">
        {rows.map((c) => (
          <div key={c.id} className="snap-start shrink-0 w-56 md:w-64">
            <Link
              to={`/reels?content=${c.id}`}
              data-testid={`pdp-reel-${c.id}`}
              className="block"
            >
              <VideoEmbed content={c} aspect="9x16" testid={`pdp-video-${c.id}`} />
              <div className="text-sm font-medium mt-2 line-clamp-2">{c.title}</div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
